import axios from 'axios';
import _includes from 'lodash/includes';
import { getCookie, lsTest } from 'utils/localstore';
import 'whatwg-fetch';
// import 'whatwg-fetch';
const CONTENT_TYPE = 'application/json';
const IMPERSONATION_SESSION_KEY = 'impersonateSessionActive';
const IMPERSONATION_LOCAL_KEYS = [
  'userBasicDetails',
  'userToken',
  'userTokenCreatedAt',
  'userId',
  'email',
  'impersonateUser',
  'adminUserId',
  'adminUserEmail',
  'adminCoordinatesLat',
  'adminCoordinatesLang',
  'isBuyingMembership',
  'isBuyingGift',
  'isBuyingFoundingMembership',
  'isBuyingPackage',
];

function clearImpersonationLocalState() {
  IMPERSONATION_LOCAL_KEYS.forEach(key => localStorage.removeItem(key));
  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
}

export const getAuthToken = () => {
  let authToken;
  if (lsTest()) {
    const isImpersonating = localStorage.getItem('impersonateUser') === 'true';
    const hasActiveSession =
      sessionStorage.getItem(IMPERSONATION_SESSION_KEY) === 'true';
    if (isImpersonating && !hasActiveSession) {
      clearImpersonationLocalState();
      return null;
    }
    authToken = localStorage.getItem('userToken');
  } else {
    authToken = getCookie('userToken');
  }
  return authToken;
};

export function buildRequestObj(
  method = 'GET',
  body = {},
  isAuthNeeded = false,
  impersonateUser = null,
) {
  const requestOptions = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': CONTENT_TYPE,
    },
  };

  if (_includes(['POST', 'PUT', 'DELETE'], method)) {
    requestOptions.body = body;
  }

  if (isAuthNeeded) {
    requestOptions.headers.Authorization = impersonateUser || getAuthToken();
  }

  return requestOptions;
}

export function buildRequestTicketingImage(
  method = 'PUT',
  body = {},
  isAuthNeeded = false,
) {
  const requestOptions = {
    method,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };

  const formData = new FormData();

  if (body.avatar) {
    const size = body.avatar.length;
    let i;
    for (i = 0; i < size; i += 1) {
      if (body.blobs[i]) {
        formData.append('file', body.blobs[i], `image${i}.jpeg`);
      } else {
        formData.append('file', body.avatar[i], `image${i}.jpeg`);
      }
    }
  }
  formData.append('comment', body.comment);

  if (_includes(['POST', 'PUT', 'DELETE'], method)) {
    requestOptions.body = formData;
  }
  if (isAuthNeeded) {
    let authToken;
    if (lsTest()) {
      authToken = localStorage.getItem('userToken');
    } else {
      authToken = getCookie('userToken');
    }
    requestOptions.headers.Authorization = authToken;
  }
  return requestOptions;
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object}          The parsed JSON from the request
 */
function parseJSON(response) {
  if (response.status === 204 || response.status === 205) {
    return {};
  }
  return response.data;
}

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
function checkStatus(response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export default function request(url, options) {
  const method = options.method.toLowerCase();
  const { headers } = options;
  const data = options.body;
  // Remove the incorrect corsDomainConfig - it won't work for CORS
  // const corsDomainConfig = {
  //   headers: {
  //     'Access-Control-Allow-Origin': ALLOWED_ORIGINS.join(', '),
  //   },
  // };

  // if (headers.Authorization) {
  //   const instance = axios.create();
  //   createAuthRefreshInterceptor(instance, refreshAuthLogic);
  //   return instance({
  //     method,
  //     url,
  //     timeout: 30000,
  //     headers,
  //     data,
  //   })
  //     .then(checkStatus)
  //     .then(parseJSON);
  // }
  return axios({
    method,
    url,
    timeout: 30000,
    headers,
    data,
    // CORS configuration - only use withCredentials if absolutely necessary
    // withCredentials: true, // Commented out to avoid CORS issues
  })
    .then(checkStatus)
    .then(parseJSON)

    .catch(error => {
      const { pathname } = window.location;
      const loginPage = [
        '/login',
        '/membership/login',
        '/register',
        '/appointment/login',
        '/appointment/register',
        '/appointment/confirm',
      ].includes(pathname);
      const isImpersonating =
        localStorage.getItem('impersonateUser') === 'true';

      const responseStatus = error.response && error.response.status;
      if (
        (responseStatus === 401 || responseStatus === 403) &&
        !loginPage &&
        !isImpersonating
      ) {
        // Clear localStorage and sessionStorage
        localStorage.clear();
        sessionStorage.clear();

        // Clear the persisted application state from localStorage
        // This ensures that when the app reinitializes, it won't load stale data
        localStorage.removeItem('applicationState');

        // Redirect to login page
        if (window && window.location) {
          window.location.replace('/login');
        }
      } else {
        // Throw a detailed error object
        const err = {
          message: error.message,
          status: responseStatus || null,
          data: (error.response && error.response.data) || null,
        };
        throw err;
      }
    });
}
